import Session from '../core/session'
import Config from '../core/config'

interface PerformanceMetrics {
  page: string
  pageLoadTime: number
  domContentLoaded: number
  firstPaint: number
  firstContentfulPaint: number
  largestContentfulPaint: number
  timeToInteractive: number
  cumulativeLayoutShift: number
  firstInputDelay?: number
}

export class PerformanceCollector {
  private static instance: PerformanceCollector
  private isTracking: boolean = false
  private clsValue: number = 0
  private clsEntries: PerformanceEntry[] = []
  private fidValue: number = 0

  private constructor() {}

  public static getInstance(): PerformanceCollector {
    if (!PerformanceCollector.instance) {
      PerformanceCollector.instance = new PerformanceCollector()
    }
    return PerformanceCollector.instance
  }

  /**
   * Start tracking performance
   */
  public start(): void {
    if (!Config.get('capturePerformance')) {
      return
    }

    if (this.isTracking) {
      return
    }

    this.isTracking = true

    // Wait for page load to complete
    if (document.readyState === 'complete') {
      this.collectMetrics()
    } else {
      window.addEventListener('load', () => {
        // Collect after a short delay to ensure all metrics are available
        setTimeout(() => this.collectMetrics(), 1000)
      })
    }

    // Track Core Web Vitals
    this.trackCoreWebVitals()
  }

  /**
   * Collect and send performance metrics
   */
  private collectMetrics(): void {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
    const paint = performance.getEntriesByType('paint')

    if (!navigation) {
      if (Config.isDebug()) {
        console.warn('[RadarSnap] Navigation timing not available')
      }
      return
    }

    const metrics: PerformanceMetrics = {
      page: window.location.pathname,
      pageLoadTime: Math.round(navigation.loadEventEnd - navigation.fetchStart),
      domContentLoaded: Math.round(navigation.domContentLoadedEventEnd - navigation.fetchStart),
      firstPaint: this.getMetricValue(paint, 'first-paint'),
      firstContentfulPaint: this.getMetricValue(paint, 'first-contentful-paint'),
      largestContentfulPaint: 0, // Will be updated by PerformanceObserver
      timeToInteractive: Math.round(navigation.domInteractive - navigation.fetchStart),
      cumulativeLayoutShift: this.clsValue,
      firstInputDelay: this.fidValue || undefined
    }

    Session.trackEvent('page_performance', metrics)

    if (Config.isDebug()) {
      console.log('[RadarSnap] Performance metrics:', metrics)
    }
  }

  /**
   * Track Core Web Vitals using PerformanceObserver
   */
  private trackCoreWebVitals(): void {
    // Track Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries()
          const lastEntry = entries[entries.length - 1] as any

          Session.trackEvent('lcp', {
            value: Math.round(lastEntry.renderTime || lastEntry.loadTime),
            element: lastEntry.element?.tagName || 'unknown'
          })
        })
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] })
      } catch (e) {
        if (Config.isDebug()) {
          console.warn('[RadarSnap] LCP observer failed:', e)
        }
      }

      // Track Cumulative Layout Shift (CLS)
      try {
        const clsObserver = new PerformanceObserver((entryList) => {
          for (const entry of entryList.getEntries()) {
            if (!(entry as any).hadRecentInput) {
              this.clsValue += (entry as any).value
              this.clsEntries.push(entry)
            }
          }
        })
        clsObserver.observe({ entryTypes: ['layout-shift'] })
      } catch (e) {
        if (Config.isDebug()) {
          console.warn('[RadarSnap] CLS observer failed:', e)
        }
      }

      // Track First Input Delay (FID)
      try {
        const fidObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries()
          const firstInput = entries[0] as any

          this.fidValue = Math.round(firstInput.processingStart - firstInput.startTime)

          Session.trackEvent('fid', {
            value: this.fidValue
          })
        })
        fidObserver.observe({ entryTypes: ['first-input'] })
      } catch (e) {
        if (Config.isDebug()) {
          console.warn('[RadarSnap] FID observer failed:', e)
        }
      }
    }
  }

  /**
   * Get metric value from PerformanceEntry array
   */
  private getMetricValue(entries: PerformanceEntry[], name: string): number {
    const entry = entries.find(e => e.name === name)
    return entry ? Math.round(entry.startTime) : 0
  }

  /**
   * Stop tracking
   */
  public stop(): void {
    this.isTracking = false
  }
}

export default PerformanceCollector.getInstance()